import { DateTime } from 'luxon';

import { DeliveryType } from '@/domain/models/Order';
import {
  SalesItemReport,
  SalesRatioReport,
  SalesReport,
} from '@/domain/models/SalesReport';
import { DASHBOARD_API_ORIGIN } from '@/global/env';
import { RestClientInterface } from '@/lib/restClient';

export interface FilterParams {
  startDate: Date;
  endDate: Date;
  masterBrandIDs?: string[];
  masterBrandNames?: string[];
  masterStoreIDs?: string[];
  deliveryServiceIds?: string[];
  deliveryTypes?: DeliveryType[];
  authStoreIds?: string[];
}

interface Interface {
  aggregateSales: (params: FilterParams) => Promise<SalesReport>;
  aggregateItemSales: (params: FilterParams) => Promise<SalesItemReport>;
  aggregateSalesRatios: (params: FilterParams) => Promise<SalesRatioReport>;
}

export class SalesReportService implements Interface {
  constructor(private readonly restClient: RestClientInterface) {}

  public async aggregateSales(params: FilterParams): Promise<SalesReport> {
    const queryParams = this.buildQueryParams(params);
    return await this.restClient.get<SalesReport>(
      `${DASHBOARD_API_ORIGIN}/api/v1/sales/reports?${queryParams.toString()}` as const
    );
  }

  public async aggregateItemSales(
    params: FilterParams
  ): Promise<SalesItemReport> {
    const queryParams = this.buildQueryParams(params);
    return await this.restClient.get<SalesItemReport>(
      `${DASHBOARD_API_ORIGIN}/api/v1/sales/items-reports?${queryParams.toString()}` as const
    );
  }

  public async aggregateSalesRatios(
    params: FilterParams
  ): Promise<SalesRatioReport> {
    const queryParams = this.buildQueryParams(params);
    return await this.restClient.get<SalesRatioReport>(
      `${DASHBOARD_API_ORIGIN}/api/v1/sales/ratios-reports?${queryParams.toString()}` as const
    );
  }

  private buildQueryParams(params: FilterParams): URLSearchParams {
    const closingDate = DateTime.fromJSDate(params.endDate)
      .plus({ days: 1 })
      .setZone('Asia/Tokyo')
      .set({
        hour: 0,
        minute: 0,
        second: 0,
      });

    const openingDate = DateTime.fromJSDate(params.startDate)
      .setZone('Asia/Tokyo')
      .set({
        hour: 0,
        minute: 0,
        second: 0,
      });

    // Convert to ISO format (JST retained)
    const openingDateISO = openingDate.toISO();
    const closingDateISO = closingDate.toISO();

    const urlParams = new URLSearchParams();

    // 型エラーが出るので、空文字を入れているが、空にはならないはず
    urlParams.append('date_from', openingDateISO || '');
    urlParams.append('date_to', closingDateISO || '');

    if (params.masterBrandIDs && params.masterBrandIDs.length > 0) {
      params.masterBrandIDs.forEach((id) =>
        urlParams.append('master_brand_ids', id)
      );
    }

    if (params.masterBrandNames && params.masterBrandNames.length > 0) {
      params.masterBrandNames.forEach((name) =>
        urlParams.append('master_brand_names', name)
      );
    }

    if (params.masterStoreIDs && params.masterStoreIDs.length > 0) {
      params.masterStoreIDs.forEach((id) =>
        urlParams.append('master_store_ids', id)
      );
    }

    if (params.deliveryServiceIds && params.deliveryServiceIds.length > 0) {
      params.deliveryServiceIds.forEach((id) =>
        urlParams.append('delivery_service_ids', id)
      );
    }

    if (params.deliveryTypes && params.deliveryTypes.length > 0) {
      params.deliveryTypes.forEach((type) =>
        urlParams.append('delivery_types', type)
      );
    }

    if (params.authStoreIds && params.authStoreIds.length > 0) {
      params.authStoreIds.forEach((id) =>
        urlParams.append('auth_store_ids', id)
      );
    }

    return urlParams;
  }
}
